import React, { useEffect, useRef, useState } from "react";
import { BASE_URL } from "utils/constant";
import {
    fetchDesignMembers,
    fetchOrganizationMembers,
} from "../../../api/design/service";

export function normalizeFileUrl(maybeUrl) {
    if (!maybeUrl) return null;
    const u = String(maybeUrl);
    if (
        u.startsWith("http://") ||
        u.startsWith("https://") ||
        u.startsWith("data:") ||
        u.startsWith("blob:")
    ) {
        return u;
    }
    return `${BASE_URL}${u.startsWith("/") ? "" : "/"}${u}`;
}

export function buildPdfFileSource(url) {
    if (!url) return null;
    const u = String(url);
    // data: or blob: urls can be passed directly
    if (u.startsWith("data:") || u.startsWith("blob:")) return u;

    const accessToken = localStorage.getItem("access");
    // If URL looks like it has an Azure SAS token, avoid Authorization header.
    const looksLikeSas =
        u.includes("sig=") ||
        u.includes("sv=") ||
        u.includes("se=") ||
        u.includes("skoid=") ||
        u.includes("sktid=") ||
        u.includes("skt=");

    if (!looksLikeSas && accessToken) {
        return { url: u, httpHeaders: { Authorization: `Bearer ${accessToken}` } };
    }
    return { url: u };
}

export function looksLikePdf(url) {
    if (!url) return false;
    const u = String(url).toLowerCase();
    return u.includes(".pdf") || u.startsWith("data:application/pdf");
}

export function displayNameFromOrgMember(m) {
    const fn = m?.user?.first_name || "";
    const ln = m?.user?.last_name || "";
    const full = `${fn} ${ln}`.trim();
    return full || m?.user?.username || m?.user?.email || `Member ${m?.id}`;
}

export function mapAssigneeIdToName(assigneeId, orgMembers) {
    const assignee = orgMembers.find((member) => member.id === assigneeId);
    return displayNameFromOrgMember(assignee);
}

export function getInitials(name) {
    if (!name) return "?";
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
}

export function UserAvatar({ member, size = "w-7 h-7" }) {
    const name = displayNameFromOrgMember(member);
    const userPic = member?.user?.userpic || member?.userpic;
    const initials = getInitials(name);

    return (
        <div
            className={`${size} rounded-full bg-purple-100 flex items-center justify-center overflow-hidden border-[1px] border-white shrink-0`}
            title={name}
        >
            {userPic ? (
                <img src={normalizeFileUrl(userPic)} alt={name} className="w-full h-full object-cover" />
            ) : (
                <span className="text-[10px] font-bold text-purple-700">{initials}</span>
            )}
        </div>
    );
}

export function useLoadAssigneesAndApprovers({ drawing, onUpdate }) {
    const onUpdateRef = useRef(onUpdate);
    useEffect(() => {
        onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    const [orgMembers, setOrgMembers] = useState([]);
    const [orgMembersLoading, setOrgMembersLoading] = useState(false);

    const [designMembers, setDesignMembers] = useState([]);
    const [designMembersLoading, setDesignMembersLoading] = useState(false);

    const refreshDesignMembers = async () => {
        if (!drawing?.id) return [];
        setDesignMembersLoading(true);
        try {
            const list = await fetchDesignMembers(drawing.id);
            setDesignMembers(list || []);
            // const assigneeDisplay = (list || []).map(displayNameFromOrgMember).join(", ") || "—";
            onUpdateRef.current?.((prev) => ({ ...(prev), assignee: list || [] }));
            return list || [];
        } catch (err) {
            console.error("Failed to refresh design members", err);
            setDesignMembers([]);
            // onUpdateRef.current?.((prev) => ({ ...(prev || drawing), assignee: "—", designMembers: [] }));
            return [];
        } finally {
            setDesignMembersLoading(false);
        }
    };

    // Load design members -> used for assignee display
    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!drawing?.id) return;
            setDesignMembersLoading(true);
            try {
                const list = await fetchDesignMembers(drawing.id);
                if (!mounted) return;
                setDesignMembers(list || []);
                // const assigneeDisplay = (list || []).map(displayNameFromOrgMember).join(", ") || "—";
                // onUpdateRef.current?.((prev) => ({ ...(prev || drawing), assignee: assigneeDisplay, designMembers: list || [] }));
            } catch (err) {
                if (!mounted) return;
                console.error("Failed to load design members", err);
                // setDesignMembers([]);
                // onUpdateRef.current?.((prev) => ({ ...(prev || drawing), assignee: "—", designMembers: [] }));
            } finally {
                if (mounted) setDesignMembersLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [drawing?.id]);

    // Load org members -> used for approver display and assignee picker
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setOrgMembersLoading(true);
                const org = JSON.parse(localStorage.getItem("organization") || "{}");
                const orgId = org?.id;
                if (!orgId) {
                    if (!mounted) return;
                    setOrgMembers([]);
                    onUpdateRef.current?.((prev) => ({ ...(prev || drawing), approver: "—", approverMembers: [] }));
                    return;
                }
                const list = await fetchOrganizationMembers(orgId);
                if (!mounted) return;
                setOrgMembers(list || []);
                const approvers = (list || []).filter((m) => m?.design_admin === true || m?.joined_as === "owner");
                const approverDisplay = approvers.map(displayNameFromOrgMember).join(", ") || "—";
                onUpdateRef.current?.((prev) => ({ ...(prev || drawing), approver: approverDisplay, approverMembers: approvers }));
            } catch (err) {
                if (!mounted) return;
                console.error("Failed to load org members", err);
                setOrgMembers([]);
                onUpdateRef.current?.((prev) => ({ ...(prev || drawing), approver: "—", approverMembers: [] }));
            } finally {
                if (mounted) setOrgMembersLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    return {
        orgMembers,
        orgMembersLoading,
        designMembers,
        designMembersLoading,
        refreshDesignMembers,
    };
}

export function useResizeWidth() {
    const ref = useRef(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        if (!ref.current) return;
        const el = ref.current;

        const update = () => setWidth(el.getBoundingClientRect().width || 0);
        update();

        const ro = new ResizeObserver(() => update());
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return { ref, width };
}
